'use client';

import { BaseTranslations } from '@hyphengroup/cms-components/baseTranslations';
import { getClientTranslations } from '@hyphengroup/cms-components/utils/translations/getClientTranslations';
import { tr } from '@hyphengroup/cms-components/utils/translations/tr';
import { useEffect, useState } from 'react';
import { useClientContext } from '$/modules/context/client';

/** Canonical below-M6 sibling section ids (CMS-1647 renders `id="section-<id>"`). */
export const TI_SIBLING_SECTION_IDS = {
  faq: 'faq',
  similarPlans: 'similar-plans',
  reviews: 'reviews',
} as const;

export type TiSiblingSectionId =
  (typeof TI_SIBLING_SECTION_IDS)[keyof typeof TI_SIBLING_SECTION_IDS];

type SiblingSectionDef = {
  id: TiSiblingSectionId;
  translationKey: string;
};

/**
 * Canonical below-M6 sibling sections in nav order (CMS-1643 / CMS-1647). CMS-1647
 * must render `id="section-<id>"` using these ids so the nav link + scroll-spy work.
 */
export const TI_SIBLING_SECTION_DEFS: SiblingSectionDef[] = [
  { id: TI_SIBLING_SECTION_IDS.faq, translationKey: 'pd_nav_faq' },
  {
    id: TI_SIBLING_SECTION_IDS.similarPlans,
    translationKey: 'pd_nav_similar_plans',
  },
  {
    id: TI_SIBLING_SECTION_IDS.reviews,
    translationKey: 'pd_nav_reviews',
  },
];

export type TiSiblingNavItem = { id: string; label: string };

const sectionElementId = (id: string) => `section-${id}`;

const sameIds = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * Post-mount detection of which below-M6 sibling sections exist in the DOM,
 * returned as translated nav items in canonical order. The DOM `id="section-<id>"`
 * is the single source of truth (also the scroll/spy target), so there is no
 * register-vs-render dual contract to keep in sync (CMS-1643 AC-7).
 *
 * `presentIds` starts empty so the first client render matches SSR — no
 * hydration mismatch; the nav reserves a fixed height so links populating
 * post-mount cause no CLS. Re-checks on resize (same trigger as scroll-spy).
 */
export const useDetectedSiblingSections = (): TiSiblingNavItem[] => {
  const { brand, lang } = useClientContext();
  const staticText = getClientTranslations(BaseTranslations, {
    brand,
    lang,
  }) as Record<string, string>;
  const [presentIds, setPresentIds] = useState<TiSiblingSectionId[]>([]);

  useEffect(() => {
    const check = () => {
      const present = TI_SIBLING_SECTION_DEFS.filter(
        (def) => document.getElementById(sectionElementId(def.id)) !== null,
      ).map((def) => def.id);
      setPresentIds((prev) => (sameIds(prev, present) ? prev : present));
    };
    check();
    window.addEventListener('resize', check, { passive: true });
    return () => window.removeEventListener('resize', check);
  }, []);

  return TI_SIBLING_SECTION_DEFS.filter((def) =>
    presentIds.includes(def.id),
  ).map((def) => ({ id: def.id, label: tr(def.translationKey, staticText) }));
};
